import type { Dispatch } from 'react';

import { MAX_FILESTACK_FILE_SIZE } from '@aline/common';
import type { NotificationsAction } from 'src/notifications';
import { NotificationsActionType } from 'src/notifications';
import { v4 as uuid4 } from 'uuid';

import { filterJunkFiles } from './filterJunkFiles';
import { validateDroppedFiles } from './validation/validateDroppedFiles';
import {
  LARGE_UPLOAD_THRESHOLD,
  MAX_REPOSITORY_UPLOAD_FILES,
} from '../../RepositoryDropzoneConfig';
import { formatBytes } from '../utils/formatBytes';

interface HandleFilesDropParams {
  dispatch: Dispatch<NotificationsAction>;
  files: File[];
  onLargeUpload: (files: File[], totalSize: number) => void;
  onValidFiles: (files: File[]) => void;
}

/**
 * Handles dropped files (as extracted by react-dropzone): junk filtering,
 * validation, and notifications. Files are uploaded exactly as dragged in.
 */
export function handleFilesDrop(params: HandleFilesDropParams): void {
  const { dispatch, files, onLargeUpload, onValidFiles } = params;

  const keptFiles = filterJunkFiles(files);

  const { invalidMimeCount, invalidMimeTypes, invalidSizeCount, tooMany, validFiles } =
    validateDroppedFiles(keptFiles);

  if (tooMany) {
    dispatch({
      payload: {
        key: uuid4(),
        message: `Too many files. You tried to upload ${keptFiles.length} files, but the maximum is ${MAX_REPOSITORY_UPLOAD_FILES}. Please select fewer files.`,
        variant: 'error',
      },
      type: NotificationsActionType.PUSH_NOTIFICATION,
    });
    return;
  }

  if (invalidSizeCount > 0) {
    dispatch({
      payload: {
        key: uuid4(),
        message: `${invalidSizeCount} ${invalidSizeCount === 1 ? 'file' : 'files'} skipped: larger than the ${formatBytes(MAX_FILESTACK_FILE_SIZE)} limit.`,
        variant: 'warning',
      },
      type: NotificationsActionType.PUSH_NOTIFICATION,
    });
  }

  if (invalidMimeCount > 0) {
    dispatch({
      payload: {
        key: uuid4(),
        message: `${invalidMimeCount} ${invalidMimeCount === 1 ? 'file' : 'files'} skipped: unsupported file ${invalidMimeTypes.length === 1 ? 'type' : 'types'} (${invalidMimeTypes.join(', ')}). Accepted: PDF, Word, images, text files.`,
        variant: 'warning',
      },
      type: NotificationsActionType.PUSH_NOTIFICATION,
    });
  }

  if (validFiles.length === 0) return;

  if (validFiles.length >= LARGE_UPLOAD_THRESHOLD) {
    const totalSize = validFiles.reduce((sum, file) => sum + file.size, 0);
    onLargeUpload(validFiles, totalSize);
    return;
  }

  onValidFiles(validFiles);
}
